import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/hooks/useAccessControl';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, BookOpen, StickyNote, GraduationCap, Plus, Trash2, LogOut, Settings, Edit2, Check, X, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentBoard() {
  const { user, logout } = useAuth();
  const { accessStatus, isTrial, daysLeft } = useAccessControl();
  const [lessons, setLessons] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const savedName = localStorage.getItem('studentName');
    if (savedName) {
      setStudentName(savedName);
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load user's lesson assignments (not exercise-level anymore)
      const assignmentsRes = await axios.get(`${API}/users/${user.id}/assignments`);
      const assignments = assignmentsRes.data;
      
      // Get unique lesson IDs
      const lessonIds = [...new Set(assignments.map(a => a.lesson_id))];
      
      // Load user's progress
      const progressRes = await axios.get(`${API}/users/${user.id}/progress`);
      const progressMap = new Map(progressRes.data.map(p => [p.exercise_id, p.status]));
      
      // Load all lessons and their exercises
      const lessonsData = [];
      for (const lessonId of lessonIds) {
        try {
          const lessonRes = await axios.get(`${API}/lessons/${lessonId}`);
          const lesson = lessonRes.data;
          
          // Load ALL exercises for this lesson
          const exercisesRes = await axios.get(`${API}/lessons/${lessonId}/exercises`);
          const exercises = exercisesRes.data.map(exercise => ({
            ...exercise,
            status: progressMap.get(exercise.id) || 'todo'
          }));
          
          lessonsData.push({
            ...lesson,
            exercises
          });
        } catch (err) {
          console.error(`Failed to load lesson ${lessonId}:`, err);
        }
      }
      
      setLessons(lessonsData);
      
      // Load user's notes
      const notesRes = await axios.get(`${API}/users/${user.id}/notes`);
      setNotes(notesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSave = () => {
    if (studentName.trim()) {
      localStorage.setItem('studentName', studentName);
      toast.success('Name saved!');
    }
  };

  const markAsDone = async (exerciseId) => {
    try {
      await axios.patch(`${API}/progress/${user.id}/${exerciseId}?status=done`);
      toast.success('Exercise marked as done!');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update progress');
    }
  };

  const markAsTodo = async (exerciseId) => {
    try {
      await axios.patch(`${API}/progress/${user.id}/${exerciseId}?status=todo`);
      toast.success('Moved back to To Do!');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update progress');
    }
  };

  const addNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      await axios.post(`${API}/notes`, {
        user_id: user.id,
        content: newNoteContent,
        is_teacher_note: false
      });
      setNewNoteContent('');
      toast.success('Note added!');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to add note');
    }
  };

  const deleteNote = async (noteId, isTeacher) => {
    if (isTeacher) {
      toast.error('Cannot delete teacher notes');
      return;
    }

    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success('Note deleted');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete note');
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  const saveEditNote = async (noteId) => {
    if (!editingNoteContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    try {
      await axios.patch(`${API}/notes/${noteId}`, {
        content: editingNoteContent
      });
      toast.success('Note updated!');
      setEditingNoteId(null);
      setEditingNoteContent('');
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update note');
    }
  };

  const todoLessons = lessons
    .map(lesson => ({
      ...lesson,
      exercises: lesson.exercises.filter(ex => ex.status === 'todo')
    }))
    .filter(lesson => lesson.exercises.length > 0);
    
  const doneLessons = lessons
    .map(lesson => ({
      ...lesson,
      exercises: lesson.exercises.filter(ex => ex.status === 'done')
    }))
    .filter(lesson => lesson.exercises.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 px-4" data-testid="student-board">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_reporterspeed/artifacts/9jftsuzl_StenoU%20Skool.png" 
              alt="StenoU" 
              className="h-20 object-contain"
              data-testid="logo"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right mr-4">
              <p className="text-sm font-medium">{user?.full_name}</p>
              <p className="text-xs text-gray-500">@{user?.username}</p>
              {user?.is_admin ? (
                <Badge className="mt-1 bg-purple-600">Admin</Badge>
              ) : accessStatus?.status === 'active' ? (
                <Badge className="mt-1 bg-green-600">Active Subscription</Badge>
              ) : accessStatus?.status === 'trial' ? (
                <Badge className="mt-1 bg-blue-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Trial: {daysLeft} days left
                </Badge>
              ) : (
                <Badge variant="outline" className="mt-1 text-orange-600 border-orange-600">No Subscription</Badge>
              )}
            </div>
            {user?.subscription_status !== 'active' && !user?.is_admin && (
              <Button
                onClick={() => navigate('/subscribe')}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Subscribe Now
              </Button>
            )}
            {user?.is_admin && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                data-testid="admin-link-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Dashboard
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Trial Alert */}
        {isTrial && daysLeft <= 3 && (
          <Alert className="mb-6 border-orange-500 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-orange-800">
                <strong>Trial ending soon!</strong> You have {daysLeft} day{daysLeft !== 1 ? 's' : ''} left in your free trial.
              </span>
              <Button
                size="sm"
                onClick={() => navigate('/subscribe')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Subscribe Now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Kanban Board */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* To Do Column */}
          <div className="bg-white/80 backdrop-blur rounded-lg p-4 shadow-lg" data-testid="todo-column">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-800">To Do</h2>
              <Badge variant="secondary" data-testid="todo-count">{todoLessons.reduce((acc, l) => acc + l.exercises.length, 0)}</Badge>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {todoLessons.map((lesson, lessonIndex) => (
                <div key={lesson.id} className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-600 px-2">{lesson.title}</h3>
                  {lesson.exercises.map((exercise, exIndex) => {
                    const cardColor = exIndex % 2 === 0 ? 'bg-blue-50' : 'bg-yellow-50';
                    return (
                      <Card
                        key={exercise.id}
                        className={`cursor-pointer hover:shadow-lg transition-all border-2 border-gray-300 hover:border-blue-500 ${cardColor}`}
                        onClick={() => navigate(`/exercise/${exercise.id}`)}
                        data-testid={`todo-card-${lessonIndex}-${exIndex}`}
                      >
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base" data-testid={`todo-card-title-${lessonIndex}-${exIndex}`}>{exercise.title}</CardTitle>
                          <CardDescription className="text-sm line-clamp-2" data-testid={`todo-card-desc-${lessonIndex}-${exIndex}`}>
                            {exercise.description}
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    );
                  })}
                </div>
              ))}
              {todoLessons.length === 0 && (
                <p className="text-center text-gray-400 py-8" data-testid="no-todo-message">No exercises assigned yet</p>
              )}
            </div>
          </div>

          {/* Notes and Reminders Column */}
          <div className="bg-white/80 backdrop-blur rounded-lg p-4 shadow-lg" data-testid="notes-column">
            <div className="flex items-center gap-2 mb-4">
              <StickyNote className="w-5 h-5 text-yellow-600" />
              <h2 className="text-xl font-bold text-gray-800">Notes & Reminders</h2>
            </div>

            {/* Add Note Form */}
            <Card className="mb-4 bg-yellow-50">
              <CardContent className="pt-4 space-y-2">
                <Textarea
                  placeholder="Add your own note..."
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="new-note-input"
                />
                <Button
                  size="sm"
                  onClick={addNote}
                  className="w-full"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </CardContent>
            </Card>

            {/* Notes List */}
            <div className="space-y-3 max-h-[calc(100vh-450px)] overflow-y-auto">
              {notes.map((note, index) => (
                <Card
                  key={note.id}
                  className={note.is_teacher ? 'bg-blue-50 border-blue-200' : 'bg-white'}
                  data-testid={`note-card-${index}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <Badge variant={note.is_teacher ? 'default' : 'secondary'} className="mb-2">
                          {note.is_teacher ? 'Teacher' : note.author}
                        </Badge>
                        {editingNoteId === note.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingNoteContent}
                              onChange={(e) => setEditingNoteContent(e.target.value)}
                              rows={3}
                              className="resize-none text-sm"
                              data-testid={`edit-note-textarea-${index}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => saveEditNote(note.id)}
                                data-testid={`save-note-btn-${index}`}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditNote}
                                data-testid={`cancel-edit-note-btn-${index}`}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap" data-testid={`note-content-${index}`}>
                            {note.content}
                          </p>
                        )}
                      </div>
                      {!note.is_teacher && editingNoteId !== note.id && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditNote(note)}
                            data-testid={`edit-note-btn-${index}`}
                          >
                            <Edit2 className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNote(note.id, note.is_teacher)}
                            data-testid={`delete-note-btn-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
              {notes.length === 0 && (
                <p className="text-center text-gray-400 py-8" data-testid="no-notes-message">No notes yet</p>
              )}
            </div>
          </div>

          {/* Done Column */}
          <div className="bg-white/80 backdrop-blur rounded-lg p-4 shadow-lg" data-testid="done-column">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-bold text-gray-800">Done</h2>
              <Badge variant="secondary" data-testid="done-count">{doneLessons.reduce((acc, l) => acc + l.exercises.length, 0)}</Badge>
            </div>
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {doneLessons.map((lesson, lessonIndex) => (
                <div key={lesson.id} className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-600 px-2">{lesson.title}</h3>
                  {lesson.exercises.map((exercise, exIndex) => (
                    <Card
                      key={exercise.id}
                      className="cursor-pointer hover:shadow-md transition-shadow bg-green-50"
                      onClick={() => navigate(`/exercise/${exercise.id}`)}
                      data-testid={`done-card-${lessonIndex}-${exIndex}`}
                    >
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2" data-testid={`done-card-title-${lessonIndex}-${exIndex}`}>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          {exercise.title}
                        </CardTitle>
                        <CardDescription className="text-sm line-clamp-2" data-testid={`done-card-desc-${lessonIndex}-${exIndex}`}>
                          {exercise.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ))}
              {doneLessons.length === 0 && (
                <p className="text-center text-gray-400 py-8" data-testid="no-done-message">No completed exercises yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}